import { create } from 'zustand';
import type { PublicFnbResponse } from '@nexacore/api-clients/fnb/public';

const STORAGE_KEY = 'fnbCustomer.orderingSession.v1';

type PublicStore = NonNullable<NonNullable<PublicFnbResponse['data']>['session']>['store'];

interface PersistedSession {
  orderingSession: string;
  expiresAt: string;
}

interface CustomerSessionState {
  orderingSession: string | null;
  expiresAt: string | null;
  store: PublicStore | null;
  requiresRevalidation: boolean;
  setSession: (orderingSession: string, expiresAt: string, store: PublicStore) => void;
  markRevalidated: () => void;
  clearSession: () => void;
}

export function isFutureCapabilityExpiry(value?: string | null): value is string {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

function readSession(): PersistedSession | null {
  if (typeof window === 'undefined') return null;

  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(STORAGE_KEY) ?? 'null'
    ) as PersistedSession | null;
    if (!value?.orderingSession || !isFutureCapabilityExpiry(value.expiresAt)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return value;
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

const restored = readSession();

export const useCustomerSessionStore = create<CustomerSessionState>((set) => ({
  orderingSession: restored?.orderingSession ?? null,
  expiresAt: restored?.expiresAt ?? null,
  store: null,
  // Store metadata is deliberately not persisted. A capability restored after a reload must
  // prove that it is still valid before any customer menu data is shown.
  requiresRevalidation: restored !== null,
  setSession: (orderingSession, expiresAt, store) => {
    if (!orderingSession || !isFutureCapabilityExpiry(expiresAt)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      set({ orderingSession: null, expiresAt: null, store: null, requiresRevalidation: false });
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ orderingSession, expiresAt }));
    set({ orderingSession, expiresAt, store, requiresRevalidation: false });
  },
  markRevalidated: () => set({ requiresRevalidation: false }),
  clearSession: () => {
    window.sessionStorage.removeItem(STORAGE_KEY);
    set({ orderingSession: null, expiresAt: null, store: null, requiresRevalidation: false });
  },
}));

export const customerSessionStorageKey = STORAGE_KEY;
